import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { pressReleaseService } from '@/lib/api/services/press-release'
import { pressReleaseCommentService } from '@/lib/api/services/press-release-comment'
import { pressReleaseReplyService } from '@/lib/api/services/press-release-reply'
import type { PaginationParams, PressReleaseFilterParams, CommentCreate, PressReleaseReplyCreate } from '@/lib/api/types'

// Query keys factory
export const pressReleaseKeys = {
  all: ['pressReleases'] as const,
  lists: () => [...pressReleaseKeys.all, 'list'] as const,
  list: (params: PaginationParams, filters: PressReleaseFilterParams) =>
    [...pressReleaseKeys.lists(), params, filters] as const,
  searches: () => [...pressReleaseKeys.all, 'search'] as const,
  search: (searchTerm: string, params: PaginationParams) =>
    [...pressReleaseKeys.searches(), searchTerm, params] as const,
  details: () => [...pressReleaseKeys.all, 'detail'] as const,
  detail: (id: string) => [...pressReleaseKeys.details(), id] as const,
  featured: () => [...pressReleaseKeys.all, 'featured'] as const,
  landingPage: () => [...pressReleaseKeys.all, 'landingPage'] as const,
  comments: (pressReleaseId: string) => [...pressReleaseKeys.all, 'comments', pressReleaseId] as const,
  replies: (commentId: string) => [...pressReleaseKeys.all, 'replies', commentId] as const,
}

/**
 * Hook to fetch paginated press releases with request-body filters.
 * Endpoint: POST /press-release/public/filter
 */
export const useFilterPressReleases = (
  params: PaginationParams = { page: 1, size: 10 },
  filters: PressReleaseFilterParams = {}
) => {
  return useQuery({
    queryKey: pressReleaseKeys.list(params, filters),
    queryFn: () => pressReleaseService.getPressReleases(params, filters),
    staleTime: 0, // Always consider data stale for immediate updates
    refetchOnMount: true, // Refetch when component mounts if data is stale
  })
}

/**
 * Backward-compatible alias for the filter endpoint hook.
 */
export const usePressReleases = (
  params: PaginationParams = { page: 1, size: 10 },
  filters: PressReleaseFilterParams = {}
) => useFilterPressReleases(params, filters)

/**
 * Hook to search press releases with pagination
 */
export const useSearchPressReleases = (
  searchTerm: string,
  params: PaginationParams = { page: 1, size: 10 }
) => {
  return useQuery({
    queryKey: pressReleaseKeys.search(searchTerm, params),
    queryFn: () => pressReleaseService.searchPressReleases(searchTerm, params),
    enabled: searchTerm.trim().length > 0,
    staleTime: 0, // Always consider data stale for immediate updates
    refetchOnMount: true, // Refetch when component mounts if data is stale
  })
}

/**
 * Hook to fetch a single press release by ID
 */
export const usePressRelease = (pressReleaseId: string | undefined) => {
  return useQuery({
    queryKey: pressReleaseKeys.detail(pressReleaseId || ''),
    queryFn: () => pressReleaseService.getPressReleaseById(pressReleaseId!),
    enabled: !!pressReleaseId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to fetch featured press releases for banner
 */
export const useFeaturedPressReleases = (params: PaginationParams = { page: 1, size: 3 }) => {
  const filters: PressReleaseFilterParams = {
    landingPage: true,
    pressRelease: true,
    pressReleaseBanner: true
  }
  return useQuery({
    queryKey: pressReleaseKeys.list(params, filters),
    queryFn: () => pressReleaseService.getFeaturedPressReleases(params),
    staleTime: 0, // Always consider data stale for immediate updates
    refetchOnMount: true, // Refetch when component mounts if data is stale
  })
}

/**
 * Hook to fetch landing page press releases
 */
export const useLandingPagePressReleases = (params: PaginationParams = { page: 1, size: 10 }) => {
  const filters: PressReleaseFilterParams = {
    landingPage: true,
    pressRelease: true,
    pressReleaseBanner: false
  }
  return useQuery({
    queryKey: pressReleaseKeys.list(params, filters),
    queryFn: () => pressReleaseService.getLandingPagePressReleases(params),
    staleTime: 0, // Always consider data stale for immediate updates
    refetchOnMount: true, // Refetch when component mounts if data is stale
  })
}

/**
 * Hook to fetch all published press releases
 */
export const usePublishedPressReleases = (
  params: PaginationParams = { page: 1, size: 10 },
  filters: PressReleaseFilterParams
) => {
  return useFilterPressReleases(params, filters)
}

/**
 * Hook to fetch comments for a press release
 */
export const usePressReleaseComments = (pressReleaseId: string | undefined) => {
  return useQuery({
    queryKey: pressReleaseKeys.comments(pressReleaseId || ''),
    queryFn: () => pressReleaseCommentService.getComments(pressReleaseId!),
    enabled: !!pressReleaseId,
    staleTime: 1 * 60 * 1000, // 1 minute
  })
}

/**
 * Hook to add a comment to a press release
 */
export const useAddPressReleaseComment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ pressReleaseId, data }: { pressReleaseId: string; data: CommentCreate }) =>
      pressReleaseCommentService.addComment(pressReleaseId, data),
    onSuccess: (_, variables) => {
      // Invalidate comments query to refetch
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.comments(variables.pressReleaseId) })
    },
  })
}

/**
 * Hook to increment view count for a press release
 */
export const useIncrementPressReleaseView = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (pressReleaseId: string) =>
      pressReleaseService.incrementView(pressReleaseId),
    onSuccess: (_, pressReleaseId) => {
      // Invalidate the detail query to refetch with updated view count
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.detail(pressReleaseId) })
    },
  })
}

/**
 * Hook to fetch replies for a comment
 */
export const usePressReleaseReplies = (commentId: string | undefined) => {
  return useQuery({
    queryKey: pressReleaseKeys.replies(commentId || ''),
    queryFn: () => pressReleaseReplyService.getReplies(commentId!),
    enabled: !!commentId,
    staleTime: 1 * 60 * 1000, // 1 minute
  })
}

/**
 * Hook to add a reply to a comment
 */
export const useAddPressReleaseReply = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ commentId, data }: { commentId: string; data: PressReleaseReplyCreate }) =>
      pressReleaseReplyService.addReply(commentId, data),
    onSuccess: (_, variables) => {
      // Invalidate replies query to refetch
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.replies(variables.commentId) })
      // Also invalidate comments to update reply count
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.comments(variables.commentId) })
    },
  })
}


/**
 * Hook to fetch home page highlights
 */
export const usePressReleaseHighlights = (type: string = 'HOME_PAGE_HERO') => {
  return useQuery({
    queryKey: [...pressReleaseKeys.all, 'highlights', type],
    queryFn: () => pressReleaseService.getHomePageHighlights(type),
    staleTime: 5 * 60 * 1000,
  })
}
